/**
 * Helpers for linking a Premium Financing profile to an Illustration OCR result.
 * linkedIllustration is stored on the pFin profile (frontend + Mongo Mixed field).
 *
 * Cash value at realization rate:
 *   (total − guaranteed) × (實現率/100) + guaranteed
 */

const toFiniteNumber = v => {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/**
 * Resolve { guaranteed, total } from a slim snapshot row or full OCR row.
 * @param {Object} row
 * @returns {{ guaranteed: number, total: number }|null}
 */
const resolveSurrenderParts = row => {
  if (!row) return null;

  // Slim snapshot
  const slimGuaranteed = toFiniteNumber(row.guaranteed);
  const slimTotal = toFiniteNumber(row.cashValue ?? row.total);
  if (slimGuaranteed != null || slimTotal != null) {
    const guaranteed = slimGuaranteed ?? 0;
    // Prefer explicit total; else reconstruct from guaranteed + nonGuaranteed
    let total = slimTotal;
    if (total == null) {
      const nonGuaranteed = toFiniteNumber(row.nonGuaranteed);
      if (nonGuaranteed != null) total = guaranteed + nonGuaranteed;
    }
    if (total == null) return null;
    return { guaranteed, total };
  }

  // Full OCR projection row
  const sv = row.surrenderValue || {};
  const guaranteed = toFiniteNumber(sv.guaranteed) ?? 0;
  const total = toFiniteNumber(sv.base?.total);
  if (total == null) return null;
  return { guaranteed, total };
};

/**
 * Build a slim linkedIllustration snapshot from a full OCR record.
 * Stores guaranteed + total so realization rate can be reapplied offline.
 * @param {Object} record - Illustration document
 * @returns {Object|null}
 */
export const buildLinkedIllustration = record => {
  if (!record) return null;
  const insurer = record.meta?.insurer;
  // Prefer slug / populated insurerId string for display; fall back to ObjectId string
  const insurerLabel =
    record.meta?.insurerCode ||
    (insurer && typeof insurer === 'object' ? insurer.insurerId || insurer._id : null) ||
    (typeof insurer === 'string' ? insurer : null);
  return {
    _id: record._id,
    meta: {
      insurer: insurerLabel,
      insurerId: insurer && typeof insurer === 'object' ? insurer._id : insurer,
      productName: record.meta?.productName ?? null,
      currency: record.meta?.currency ?? null,
      premium: {
        totalBasicPremium: record.meta?.premium?.totalBasicPremium ?? null
      },
      initialSurrenderValue: record.meta?.initialSurrenderValue ?? null
    },
    projection: (Array.isArray(record.projection) ? record.projection : []).map(row => {
      const parts = resolveSurrenderParts(row);
      const guaranteed = parts ? parts.guaranteed : null;
      const total = parts ? parts.total : null;
      return {
        policyYear: row.policyYear ?? null,
        guaranteed,
        // nonGuaranteed kept for readability / older readers
        nonGuaranteed: parts != null ? total - guaranteed : null,
        cashValue: total
      };
    })
  };
};

/**
 * Whether the linked illustration has a projection row for a given policy year.
 * Used to warn (instead of silently doing nothing) when yearsToShow asks for a
 * year the OCR'd illustration doesn't cover.
 * @param {Object} linkedIllustration
 * @param {number} year
 * @returns {boolean}
 */
export const hasIllustrationYear = (linkedIllustration, year) => {
  if (!linkedIllustration || year == null || year === '') return false;
  const projection = Array.isArray(linkedIllustration.projection) ? linkedIllustration.projection : [];
  const yearNum = Number(year);
  return projection.some(r => Number(r.policyYear) === yearNum);
};

/**
 * Human-friendly label for a linked illustration snapshot (product > insurer).
 * @param {Object} linkedIllustration
 * @returns {string|null}
 */
export const getLinkedIllustrationLabel = linkedIllustration => {
  if (!linkedIllustration) return null;
  return linkedIllustration.meta?.productName || linkedIllustration.meta?.insurer || null;
};

/**
 * Cash value for a policy year:
 *   (total − guaranteed) × (returnRealizationRate/100) + guaranteed
 *
 * @param {Object} linkedIllustration
 * @param {number} year
 * @param {number} [returnRealizationRate=100]
 * @returns {number|null}
 */
export const getIllustrationCashValueAtYear = (linkedIllustration, year, returnRealizationRate = 100) => {
  if (!linkedIllustration || year == null || year === '') return null;
  const projection = Array.isArray(linkedIllustration.projection) ? linkedIllustration.projection : [];
  const yearNum = Number(year);
  const row = projection.find(r => Number(r.policyYear) === yearNum);
  if (!row) return null;

  const parts = resolveSurrenderParts(row);
  if (!parts) return null;

  const { guaranteed, total } = parts;
  const rate = Number(returnRealizationRate);
  const factor = Number.isFinite(rate) ? rate / 100 : 1;
  return Math.round((total - guaranteed) * factor + guaranteed);
};

/**
 * Breakdown of the surrender value for a policy year, respecting realization rate:
 *   guaranteed              = guaranteed cash value
 *   nonGuaranteed           = (total − guaranteed) × (returnRealizationRate/100)
 *   expectedSurrenderValue  = guaranteed + nonGuaranteed
 *
 * @param {Object} linkedIllustration
 * @param {number} year
 * @param {number} [returnRealizationRate=100]
 * @returns {{ guaranteed: number, nonGuaranteed: number, expectedSurrenderValue: number }|null}
 */
export const getIllustrationBreakdownAtYear = (linkedIllustration, year, returnRealizationRate = 100) => {
  if (!linkedIllustration || year == null || year === '') return null;
  const projection = Array.isArray(linkedIllustration.projection) ? linkedIllustration.projection : [];
  const yearNum = Number(year);
  const row = projection.find(r => Number(r.policyYear) === yearNum);
  if (!row) return null;

  const parts = resolveSurrenderParts(row);
  if (!parts) return null;

  const { guaranteed, total } = parts;
  const rate = Number(returnRealizationRate);
  const factor = Number.isFinite(rate) ? rate / 100 : 1;
  const nonGuaranteed = Math.round((total - guaranteed) * factor);
  return {
    guaranteed: Math.round(guaranteed),
    nonGuaranteed,
    expectedSurrenderValue: Math.round(guaranteed) + nonGuaranteed
  };
};

/**
 * Rebuild cashValueAt from yearsToShow using linkedIllustration + realization rate.
 * Index 0 is left untouched (day-0 / unused slot).
 * @returns {number[]|null} updated array, or null if nothing changed / no link
 */
export const refreshCashValuesFromLinkedIllustration = (
  yearsToShow,
  cashValueAt,
  linkedIllustration,
  returnRealizationRate = 100
) => {
  if (!linkedIllustration || !yearsToShow || !cashValueAt) return null;

  const next = [...cashValueAt];
  let changed = false;

  yearsToShow.forEach((year, index) => {
    if (index === 0 || year == null) return;
    const cashValue = getIllustrationCashValueAtYear(linkedIllustration, year, returnRealizationRate);
    if (cashValue != null && next[index] !== cashValue) {
      next[index] = cashValue;
      changed = true;
    }
  });

  return changed ? next : null;
};
